import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react'
import * as React from 'react'
import { DayPicker } from 'react-day-picker'

import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'

function Calendar({ className, classNames, showOutsideDays = true, ...props }) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'p-4 bg-white backdrop-blur-lg rounded-xl border shadow-lg w-full md:w-auto',
        className
      )}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-6',
        month: 'flex flex-col gap-4',
        caption: 'flex justify-between items-center mb-4 px-2 relative',
        caption_label: 'sr-only', // Hide default caption label
        nav: 'flex items-center gap-1',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-8 w-8 bg-transparent p-0 hover:bg-accent/50 bg-heading-primary text-white border-2 border-border/50'
        ),
        nav_button_previous: 'absolute left-2',
        nav_button_next: 'absolute right-2',
        table: 'w-full border-collapse',
        head_row: 'grid grid-cols-7 gap-px mb-2',
        head_cell: 'text-muted-foreground font-medium text-sm h-8 flex items-center justify-center',
        row: 'grid grid-cols-7 gap-px mt-1',
        cell: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md'
            : ''
        ),
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-10 w-full rounded-lg font-normal transition-colors',
          'hover:bg-primary/10 hover:text-primary',
          'focus:outline-none focus:ring-2 focus:ring-primary/50',
          'aria-selected:bg-primary aria-selected:text-primary-foreground',
          'aria-selected:hover:bg-primary/90'
        ),
        day_range_start: 'day-range-start rounded-l-lg bg-gradient-to-r from-primary/20',
        day_range_end: 'day-range-end rounded-r-lg bg-gradient-to-l from-primary/20',
        day_selected: 'bg-primary text-primary-foreground shadow-md hover:bg-primary/90',
        day_today: 'bg-accent/50 border-2 border-primary/20 text-foreground',
        day_outside: 'text-muted-foreground/50 aria-selected:text-muted-foreground/50',
        day_disabled: 'text-muted-foreground/30 pointer-events-none',
        day_range_middle: 'aria-selected:bg-primary/20 aria-selected:text-primary',
        day_hidden: 'invisible',
        caption_dropdowns: 'flex gap-2 items-center justify-center', // Keep dropdowns visible
        dropdown: cn(
          'px-2 py-1 pr-8 rounded-lg border border-border bg-background',
          'hover:bg-accent/50 transition-colors appearance-none relative'
        ),
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
        Dropdown: ({ children, ...props }) => (
          <div className="relative ml-7.5 w-full">
            <select
              {...props}
              className={cn(
                'px-2 py-1 pr-8 rounded-lg border border-border bg-background hover:bg-accent/50 transition-colors appearance-none',
                classNames?.dropdown
              )}
            >
              {children}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-muted-foreground" />
          </div>
        ),
      }}
      {...props}
    />
  )
}

export { Calendar }
